/**
 * Anagrafiche RENTRI — verifica iscrizione/Albo, anagrafica completa e unità locali.
 *
 * Flusso d'uso:
 *   1. L'utente inserisce CF o P.IVA dell'operatore.
 *   2. Si chiama in parallelo:
 *        - /anagrafiche/operatori?q=<CF>     → anagrafica completa (denominazione,
 *          sede, PEC, num_iscr operatore, ecc.)
 *        - /anagrafiche/operatore/<CF>/controllo-iscrizione        → boolean
 *        - /anagrafiche/operatore/<CF>/controllo-autorizzazione-albo → boolean
 *   3. Quando l'anagrafica è disponibile, dal `num_iscr` si carica
 *      /anagrafiche/operatore/<num_iscr>/siti.
 *
 * NB: l'API RENTRI è incoerente: i due endpoint "controllo-*" vogliono il CF/PIVA,
 *     mentre /siti vuole il `num_iscr` (es. OP2602VAF081537). Qui lo nascondiamo.
 */
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Search,
  RefreshCw,
  MapPin,
  ShieldCheck,
  CheckCircle2,
  XCircle,
  Award,
  Info,
  Building2,
  Mail,
} from "lucide-react";
import { endpoints } from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";
import { CenteredLoader, ErrorState, EmptyState } from "@/components/ui/feedback";
import { Badge } from "@/components/ui/badge";

interface Operatore {
  num_iscr?: string;
  identificativo?: string;
  denominazione?: string;
  forma_giuridica_id?: string;
  piva?: string;
  cciaarea?: string;
  nrea?: string;
  data_iscrizione_ri?: string;
  stato_impresa_ri?: string;
  pec?: string;
  citta_sl?: string;
  indirizzo_sl?: string;
  civico_sl?: string;
  capsl?: string;
  provincia_slid?: string;
  cognome_lr?: string;
  nome_lr?: string;
  cod_fisc_lr?: string;
  data_iscrizione?: string;
  profilo_soggetto?: string;
}

interface Sito {
  num_iscr_sito?: string;
  num_iscr_operatore?: string;
  nome?: string;
  is_sede_legale?: boolean;
  comune_id?: string;
  provincia_id?: string;
  indirizzo?: string;
  civico?: string;
  attivita?: string[];
  stato?: string;
}

export function Anagrafiche() {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState<string | null>(null);

  // 1) Anagrafica completa (la chiave /operatori?q=<CF> torna sempre uno o zero risultati)
  const operatoriQ = useQuery({
    queryKey: ["operatori-anag", active],
    queryFn: () => endpoints.anagrafiche.operatori(active!),
    enabled: !!active,
    retry: false,
  });
  const operatore = (Array.isArray(operatoriQ.data) ? operatoriQ.data[0] : undefined) as
    | Operatore
    | undefined;
  const numIscr = operatore?.num_iscr ?? null;

  // 2) Iscrizione + Albo (richiedono CF/PIVA)
  const checkIscrQ = useQuery({
    queryKey: ["check-iscr", active],
    queryFn: () => endpoints.anagrafiche.controlloIscrizione(active!),
    enabled: !!active,
    retry: false,
  });
  const checkAlboQ = useQuery({
    queryKey: ["check-albo", active],
    queryFn: () => endpoints.anagrafiche.controlloAutorizzazioneAlbo(active!),
    enabled: !!active,
    retry: false,
  });

  // 3) Siti (richiede num_iscr, ottenuto dall'anagrafica)
  const sitiQ = useQuery({
    queryKey: ["siti", numIscr],
    queryFn: () => endpoints.anagrafiche.siti(numIscr!),
    enabled: !!numIscr,
    retry: false,
  });

  const onSearch = () => setActive(query.trim() || null);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Anagrafiche RENTRI</h1>
        <p className="text-sm text-muted-foreground">
          Verifica iscrizione, Albo Gestori, anagrafica completa e unità locali
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Cerca operatore</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <label className="text-xs font-medium text-muted-foreground">
                Codice fiscale o partita IVA
              </label>
              <Input
                placeholder="CF o P.IVA operatore"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && onSearch()}
                className="font-mono"
              />
            </div>
            <Button onClick={onSearch}>
              <Search className="mr-2 h-4 w-4" /> Verifica
            </Button>
          </div>
          <p className="flex items-start gap-2 text-xs text-muted-foreground">
            <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
            L'API RENTRI non supporta ricerca per nome. Per cercare un operatore per
            denominazione usa la{" "}
            <Link to="/contacts" className="underline">
              Rubrica locale
            </Link>
            .
          </p>
        </CardContent>
      </Card>

      {active && (
        <div className="grid gap-4 md:grid-cols-2">
          <ResultCard
            icon={<ShieldCheck className="h-5 w-5" />}
            title="Iscrizione RENTRI"
            isLoading={checkIscrQ.isLoading}
            error={checkIscrQ.error}
            value={checkIscrQ.data as boolean | undefined}
            onRefresh={() => checkIscrQ.refetch()}
            okLabel="Operatore iscritto"
            koLabel="Operatore non iscritto"
          />
          <ResultCard
            icon={<Award className="h-5 w-5" />}
            title="Albo Gestori Ambientali"
            isLoading={checkAlboQ.isLoading}
            error={checkAlboQ.error}
            value={checkAlboQ.data as boolean | undefined}
            onRefresh={() => checkAlboQ.refetch()}
            okLabel="Iscrizione Albo valida"
            koLabel="Nessuna iscrizione Albo"
          />
        </div>
      )}

      {active && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="h-5 w-5" /> Anagrafica
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => operatoriQ.refetch()}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {operatoriQ.isLoading && <CenteredLoader />}
            {operatoriQ.error && <ErrorState error={operatoriQ.error} />}
            {!operatoriQ.isLoading && !operatoriQ.error && !operatore && (
              <EmptyState
                title="Operatore non trovato"
                description="Verifica il codice fiscale o la partita IVA."
              />
            )}
            {operatore && (
              <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                <Field label="Denominazione" value={operatore.denominazione} />
                <Field label="N. iscrizione RENTRI" value={operatore.num_iscr} mono />
                <Field
                  label="CF / P.IVA"
                  value={operatore.identificativo ?? operatore.piva}
                  mono
                />
                <Field
                  label="Profilo soggetto"
                  value={operatore.profilo_soggetto}
                />
                <Field
                  label="Sede legale"
                  value={
                    operatore.indirizzo_sl
                      ? `${operatore.indirizzo_sl} ${operatore.civico_sl ?? ""}, ${
                          operatore.capsl ?? ""
                        } ${operatore.citta_sl ?? ""}`
                      : undefined
                  }
                />
                <Field
                  label="Legale rappresentante"
                  value={
                    operatore.cognome_lr
                      ? `${operatore.cognome_lr} ${operatore.nome_lr ?? ""} (${
                          operatore.cod_fisc_lr ?? "—"
                        })`
                      : undefined
                  }
                />
                <Field
                  label="REA"
                  value={
                    operatore.cciaarea && operatore.nrea
                      ? `${operatore.cciaarea}-${operatore.nrea}`
                      : operatore.nrea
                  }
                  mono
                />
                <Field
                  label="Iscrizione RENTRI dal"
                  value={
                    operatore.data_iscrizione
                      ? new Date(operatore.data_iscrizione).toLocaleDateString("it-IT")
                      : undefined
                  }
                />
                {operatore.pec && (
                  <div className="sm:col-span-2 flex items-center gap-2 rounded-md bg-muted/40 p-2 text-xs">
                    <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="font-mono">{operatore.pec}</span>
                  </div>
                )}
              </dl>
            )}
          </CardContent>
        </Card>
      )}

      {active && numIscr && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <MapPin className="h-5 w-5" /> Unità locali
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => sitiQ.refetch()}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {sitiQ.isLoading && <CenteredLoader />}
            {sitiQ.error && <ErrorState error={sitiQ.error} />}
            {Array.isArray(sitiQ.data) && (sitiQ.data as Sito[]).length > 0 ? (
              <Table>
                <THead>
                  <TR>
                    <TH>N. iscrizione sito</TH>
                    <TH>Nome</TH>
                    <TH>Indirizzo</TH>
                    <TH>Attività</TH>
                    <TH>Stato</TH>
                  </TR>
                </THead>
                <TBody>
                  {(sitiQ.data as Sito[]).map((s, i) => (
                    <TR key={i}>
                      <TD className="font-mono text-xs">
                        {s.num_iscr_sito ?? "—"}
                        {s.is_sede_legale && (
                          <Badge variant="outline" className="ml-2 text-[10px]">
                            sede legale
                          </Badge>
                        )}
                      </TD>
                      <TD>{s.nome ?? "—"}</TD>
                      <TD className="text-xs">
                        {s.indirizzo ? `${s.indirizzo} ${s.civico ?? ""}` : "—"}
                        {s.comune_id ? (
                          <span className="ml-1 text-muted-foreground">
                            ({s.comune_id})
                          </span>
                        ) : null}
                      </TD>
                      <TD className="text-xs">
                        {Array.isArray(s.attivita) && s.attivita.length > 0
                          ? s.attivita.join(", ")
                          : "—"}
                      </TD>
                      <TD>
                        <Badge
                          variant={s.stato === "Attivo" ? "secondary" : "outline"}
                        >
                          {s.stato ?? "—"}
                        </Badge>
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            ) : (
              !sitiQ.isLoading && !sitiQ.error && (
                <EmptyState
                  title="Nessuna unità locale"
                  description="L'operatore non ha unità locali registrate per questo certificato."
                />
              )
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value?: string | null;
  mono?: boolean;
}) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className={`mt-0.5 text-sm ${mono ? "font-mono" : ""}`}>
        {value ?? <span className="text-muted-foreground">—</span>}
      </dd>
    </div>
  );
}

function ResultCard({
  icon,
  title,
  isLoading,
  error,
  value,
  onRefresh,
  okLabel,
  koLabel,
}: {
  icon: React.ReactNode;
  title: string;
  isLoading: boolean;
  error: unknown;
  value: boolean | undefined;
  onRefresh: () => void;
  okLabel: string;
  koLabel: string;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            {icon} {title}
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={onRefresh}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && <CenteredLoader />}
        {!!error && <ErrorState error={error} />}
        {!isLoading && !error && value === true && (
          <div className="flex items-center gap-3 rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-emerald-700 dark:text-emerald-400">
            <CheckCircle2 className="h-5 w-5" />
            <span className="text-sm font-medium">{okLabel}</span>
          </div>
        )}
        {!isLoading && !error && value === false && (
          <div className="flex items-center gap-3 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-amber-700 dark:text-amber-400">
            <XCircle className="h-5 w-5" />
            <span className="text-sm font-medium">{koLabel}</span>
          </div>
        )}
        {!isLoading && !error && value === undefined && (
          <p className="text-sm text-muted-foreground">In attesa di dati…</p>
        )}
      </CardContent>
    </Card>
  );
}
